type BrandSegmentState = {
  element: SVGPathElement;
  current: number;
  target: number;
};

export function initHomeBrand(): void {
  const hero = document.querySelector<HTMLElement>("#welcome");
  const brand = document.querySelector<HTMLElement>("[data-home-brand]");
  const segments = Array.from(
    document.querySelectorAll<SVGPathElement>("[data-home-brand-segment]"),
  );

  if (!hero || !brand || segments.length === 0) return;

  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const states: BrandSegmentState[] = segments.map((element) => ({
    element,
    current: 0,
    target: 0,
  }));

  let interactionReady = reducedMotion.matches;
  let interactionActive = false;
  let eraseOrder = states.map((_, index) => index);
  let frame = 0;

  const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

  const smoothstep = (edgeStart: number, edgeEnd: number, value: number): number => {
    if (edgeStart === edgeEnd) return value >= edgeEnd ? 1 : 0;

    const normalized = clamp01((value - edgeStart) / (edgeEnd - edgeStart));
    return normalized * normalized * (3 - 2 * normalized);
  };

  const setInteractiveState = (): void => {
    states.forEach(({ element }) => {
      element.style.strokeDashoffset = "0";
    });

    interactionReady = true;
  };

  if (reducedMotion.matches) {
    setInteractiveState();
  } else {
    let completed = 0;

    segments.forEach((element) => {
      element.addEventListener(
        "animationend",
        (event) => {
          if (event.animationName !== "homeBrandSegmentIn") return;

          completed += 1;
          if (completed === states.length) setInteractiveState();
        },
        { once: true },
      );
    });
  }

  const getDistanceToPath = (
    path: SVGPathElement,
    pointerX: number,
    pointerY: number,
  ): number => {
    const matrix = path.getScreenCTM();
    if (!matrix) return Number.POSITIVE_INFINITY;

    const pathLength = path.getTotalLength();
    const sampleCount = 24;
    let nearest = Number.POSITIVE_INFINITY;

    for (let sample = 0; sample <= sampleCount; sample += 1) {
      const point = path
        .getPointAtLength((pathLength * sample) / sampleCount)
        .matrixTransform(matrix);

      nearest = Math.min(nearest, Math.hypot(pointerX - point.x, pointerY - point.y));
    }

    return nearest;
  };

  const render = (): void => {
    let needsAnotherFrame = false;

    states.forEach((state) => {
      const difference = state.target - state.current;
      const damping = difference > 0 ? 0.16 : 0.095;

      state.current += difference * damping;

      if (Math.abs(difference) < 0.0008) {
        state.current = state.target;
      } else {
        needsAnotherFrame = true;
      }

      state.element.style.strokeDashoffset = clamp01(state.current).toFixed(4);
    });

    frame = needsAnotherFrame ? requestAnimationFrame(render) : 0;
  };

  const requestFrame = (): void => {
    if (reducedMotion.matches) {
      states.forEach((state) => {
        state.current = 0;
        state.target = 0;
        state.element.style.strokeDashoffset = "0";
      });
      return;
    }

    if (!frame) frame = requestAnimationFrame(render);
  };

  const restore = (): void => {
    interactionActive = false;

    states.forEach((state) => {
      state.target = 0;
    });

    requestFrame();
  };

  const update = (event: PointerEvent): void => {
    if (!finePointer.matches || reducedMotion.matches || !interactionReady) return;

    const rect = brand.getBoundingClientRect();
    const outerRadius = Math.max(rect.width * 0.76, 260);
    const innerRadius = Math.max(rect.width * 0.13, 46);

    const distances = states.map(({ element }) =>
      getDistanceToPath(element, event.clientX, event.clientY),
    );

    const nearestDistance = Math.min(...distances);

    if (!interactionActive && nearestDistance < outerRadius) {
      interactionActive = true;
      eraseOrder = distances
        .map((distance, index) => ({ distance, index }))
        .sort((first, second) => first.distance - second.distance)
        .map(({ index }) => index);
    }

    if (interactionActive && nearestDistance > outerRadius * 1.14) {
      restore();
      return;
    }

    if (!interactionActive) return;

    const proximity = clamp01(
      1 - (nearestDistance - innerRadius) / (outerRadius - innerRadius),
    );

    const stages: ReadonlyArray<readonly [number, number]> = [
      [0.04, 0.44],
      [0.28, 0.7],
      [0.54, 0.94],
    ];

    eraseOrder.forEach((segmentIndex, orderIndex) => {
      const stage = stages[orderIndex];
      const state = states[segmentIndex];
      if (!stage || !state) return;

      state.target = smoothstep(stage[0], stage[1], proximity);
    });

    requestFrame();
  };

  hero.addEventListener("pointermove", update, { passive: true });
  hero.addEventListener("pointerleave", restore);
  finePointer.addEventListener("change", restore);

  reducedMotion.addEventListener("change", (event) => {
    if (event.matches) {
      setInteractiveState();
      restore();
    }
  });
}
